import React, { useMemo, useState } from 'react'
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  LinearProgress,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { formatUserDisplayName } from '../../../../utility/userName'

const isTruthy = (value) => value === true || value === 'true' || value === 1 || value === '1'

const formatDate = (value) => {
  if (!value) return '-'
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return String(value)
  return dt.toLocaleDateString('en-GB')
}

const getOptionLabel = (options, value) => options.find((opt) => opt.value === value)?.label || ''

const renderSelectChip = (options, value) => {
  const label = getOptionLabel(options, value)
  if (!label) {
    return (
      <Typography variant="body2" color="text.secondary" noWrap>
        -
      </Typography>
    )
  }
  return <Chip label={label} size="small" variant="outlined" />
}

const renderText = (value) => {
  if (value == null || value === '') {
    return (
      <Typography variant="body2" color="text.secondary" noWrap>
        -
      </Typography>
    )
  }
  return (
    <Typography variant="body2" noWrap>
      {String(value)}
    </Typography>
  )
}

const UserTable = ({
  rows,
  total,
  loading,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  listConfigs,
  allUsers,
  onEditClick,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}) => {
  const columns = useMemo(() => {
    const categoryOptions =
      listConfigs?.category?.items?.map((it) => ({ value: it.id, label: it.value })) || []
    const userTypeOptions =
      listConfigs?.user_type?.items?.map((it) => ({ value: it.id, label: it.value })) || []
    const workStatusOptions =
      listConfigs?.work_status?.items?.map((it) => ({ value: it.id, label: it.value })) || []
    const departmentOptions =
      listConfigs?.department?.items?.map((it) => ({ value: it.id, label: it.value })) || []
    const designationOptions =
      listConfigs?.designation?.items?.map((it) => ({ value: it.id, label: it.value })) || []
    const subDepartmentOptions =
      listConfigs?.sub_department?.items?.map((it) => ({ value: it.id, label: it.value })) || []
    const genderOptions =
      listConfigs?.gender?.items?.map((it) => ({ value: it.id, label: it.value })) || []
    const bloodGroupOptions =
      listConfigs?.blood_group?.items?.map((it) => ({ value: it.id, label: it.value })) || []
    const reportingManagerOptions = (allUsers || []).map((it) => ({
      value: it.id,
      label: formatUserDisplayName(it),
    }))

    return [
      {
        field: 'user_id',
        header: 'User ID',
        minWidth: 130,
        sortable: true,
        render: (row) => renderText(row.id),
      },
      {
        field: 'full_name',
        header: 'Full Name',
        minWidth: 200,
        sortable: false,
        alwaysVisible: false, // remove this if you want it toggleable
        render: (row) =>
          renderText([row.first_name, row.middle_name, row.last_name].filter(Boolean).join(' ')),
      },
      {
        field: 'first_name',
        header: 'First Name',
        minWidth: 140,
        sortable: true,
        render: (row) => renderText(row.first_name),
      },
      {
        field: 'middle_name',
        header: 'Middle Name',
        minWidth: 130,
        sortable: true,
        render: (row) => renderText(row.middle_name),
      },
      {
        field: 'last_name',
        header: 'Last Name',
        minWidth: 140,
        sortable: true,
        render: (row) => renderText(row.last_name),
      },
      {
        field: 'email',
        header: 'Email',
        minWidth: 220,
        sortable: true,
        render: (row) => renderText(row.email),
      },
      {
        field: 'is_admin',
        header: 'Admin',
        minWidth: 90,
        sortable: true,
        render: (row) =>
          isTruthy(row.is_admin) ? (
            <Chip label="Yes" size="small" color="success" />
          ) : (
            <Chip label="No" size="small" />
          ),
      },
      {
        field: 'employee_id',
        header: 'EMP ID',
        minWidth: 120,
        sortable: true,
        render: (row) => renderText(row.employee_id),
      },
      {
        field: 'category',
        header: 'Category',
        minWidth: 100,
        sortable: true,
        render: (row) => renderSelectChip(categoryOptions, row.category),
      },
      {
        field: 'user_type',
        header: 'User Type',
        minWidth: 110,
        sortable: true,
        render: (row) => renderSelectChip(userTypeOptions, row.user_type),
      },
      {
        field: 'work_status',
        header: 'Work Status',
        minWidth: 140,
        sortable: true,
        render: (row) => renderSelectChip(workStatusOptions, row.work_status),
      },
      {
        field: 'department',
        header: 'Department',
        minWidth: 140,
        sortable: true,
        render: (row) => renderSelectChip(departmentOptions, row.department),
      },
      {
        field: 'sub_department',
        header: 'Sub Department',
        minWidth: 160,
        sortable: true,
        render: (row) => renderSelectChip(subDepartmentOptions, row.sub_department),
      },
      {
        field: 'designation',
        header: 'Designation',
        minWidth: 140,
        sortable: true,
        render: (row) => renderSelectChip(designationOptions, row.designation),
      },
      {
        field: 'gender',
        header: 'Gender',
        minWidth: 110,
        sortable: true,
        render: (row) => renderSelectChip(genderOptions, row.gender),
      },
      {
        field: 'blood_group',
        header: 'Blood Group',
        minWidth: 110,
        sortable: true,
        render: (row) => renderSelectChip(bloodGroupOptions, row.blood_group),
      },
      {
        field: 'reporting_manager_id',
        header: 'Reporting Manager',
        minWidth: 200,
        sortable: true,
        render: (row) =>
          renderText(getOptionLabel(reportingManagerOptions, row.reporting_manager_id)),
      },
      {
        field: 'primary_contact',
        header: 'Primary Contact',
        minWidth: 140,
        sortable: true,
        render: (row) => renderText(row.primary_contact),
      },
      {
        field: 'office_contact',
        header: 'Office Contact',
        minWidth: 140,
        sortable: true,
        render: (row) => renderText(row.office_contact),
      },
      {
        field: 'date_of_joining',
        header: 'Date of Joining',
        minWidth: 140,
        sortable: true,
        render: (row) => renderText(formatDate(row.date_of_joining)),
      },
      {
        field: 'date_of_birth',
        header: 'Date of Birth',
        minWidth: 130,
        sortable: true,
        render: (row) => renderText(formatDate(row.date_of_birth)),
      },
      {
        field: 'date_of_leaving',
        header: 'Date of Leaving',
        minWidth: 140,
        sortable: true,
        render: (row) => renderText(formatDate(row.date_of_leaving)),
      },
      {
        field: 'notice_period_end_date',
        header: 'Notice Period End',
        minWidth: 150,
        sortable: true,
        render: (row) => renderText(formatDate(row.notice_period_end_date)),
      },
      {
        field: 'probation_end_date',
        header: 'Probation End',
        minWidth: 140,
        sortable: true,
        render: (row) => renderText(formatDate(row.probation_end_date)),
      },
      {
        field: 'aadhar_number',
        header: 'Aadhar Number',
        minWidth: 150,
        sortable: true,
        render: (row) => renderText(row.aadhar_number),
      },
      {
        field: 'pan_number',
        header: 'PAN Number',
        minWidth: 130,
        sortable: true,
        render: (row) => renderText(row.pan_number),
      },
      {
        field: 'permanent_address',
        header: 'Permanent Address',
        minWidth: 220,
        sortable: true,
        render: (row) => renderText(row.permanent_address),
      },
      {
        field: 'temporary_address',
        header: 'Temporary Address',
        minWidth: 220,
        sortable: true,
        render: (row) => renderText(row.temporary_address),
      },
      {
        field: 'short_bio',
        header: 'Short Bio',
        minWidth: 220,
        sortable: true,
        render: (row) => renderText(row.short_bio),
      },
      {
        field: 'is_account_active',
        header: 'Account',
        minWidth: 110,
        sortable: true,
        render: (row) =>
          isTruthy(row.is_account_active) ? (
            <Chip label="Active" size="small" color="success" />
          ) : (
            <Chip label="Inactive" size="small" color="error" />
          ),
      },
      {
        field: 'actions',
        header: '',
        minWidth: 64,
        sortable: false,
        alwaysVisible: true,
        render: (row) => (
          <Tooltip title="Edit User">
            <IconButton size="small" onClick={() => onEditClick(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ]
  }, [allUsers, listConfigs, onEditClick])

  const sortField = sortModel?.[0]?.field || 'first_name'
  const sortOrder = sortModel?.[0]?.sort || 'asc'

  const visibleColumns = columns.filter(
    (col) => col.alwaysVisible || columnVisibilityModel?.[col.field] !== false,
  )
  const tableMinWidth = useMemo(
    () => visibleColumns.reduce((totalWidth, col) => totalWidth + (col.minWidth || 120), 0),
    [visibleColumns],
  )

  const handleSort = (field, sortable) => {
    if (!sortable) return
    const nextSort = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc'
    onSortModelChange([{ field, sort: nextSort }])
  }

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 236px)', overflowX: 'auto' }}>
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: tableMinWidth, width: 'max-content', tableLayout: 'fixed' }}
        >
          <TableHead>
            <TableRow>
              {visibleColumns.map((col) => {
                const active = sortField === col.field
                return (
                  <TableCell
                    key={col.field}
                    sx={{
                      minWidth: col.minWidth,
                      width: col.minWidth,
                      maxWidth: col.minWidth,
                      backgroundColor: 'grey.100',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={active}
                        direction={active ? sortOrder : 'asc'}
                        onClick={() => handleSort(col.field, col.sortable)}
                      >
                        {col.header}
                      </TableSortLabel>
                    ) : (
                      col.header
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2, textAlign: 'center' }}
                  >
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onDoubleClick={() => onEditClick(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={`${row.id}-${col.field}`}
                      sx={{
                        minWidth: col.minWidth,
                        width: col.minWidth,
                        maxWidth: col.minWidth,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        sx={{
          '.MuiTablePagination-toolbar': {
            minHeight: 35,
            px: 1,
          },
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            fontSize: '0.8rem',
          },
          '.MuiTablePagination-actions': {
            ml: 0.5,
          },
        }}
        count={total}
        page={paginationModel.page}
        onPageChange={(_, newPage) =>
          onPaginationModelChange({
            page: newPage,
            pageSize: paginationModel.pageSize,
          })
        }
        rowsPerPage={paginationModel.pageSize}
        onRowsPerPageChange={(e) =>
          onPaginationModelChange({
            page: 0,
            pageSize: Number(e.target.value),
          })
        }
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  )
}

export default UserTable
